/**
 * Max-weight bipartite assignment — ADR-004 §2.2: GLOBAL optimization, not
 * greedy first-match. Kuhn–Munkres (Hungarian) with row/column potentials,
 * O(rows²·cols); rows may stay unassigned (a zero-weight dummy column per
 * row), so a bone with no positive-score candidate simply maps to nothing.
 * Depends on: nothing. Depended on by: kernel/mapping/map-rig.
 */

/**
 * scores[row][col] ≥ 0; entries ≤ 0 are "no candidate". Returns, per row,
 * the assigned column or null. Deterministic for a fixed input order.
 */
export function maxWeightAssign(scores: ReadonlyArray<ReadonlyArray<number>>): Array<number | null> {
  const rows = scores.length;
  if (rows === 0) return [];
  const realCols = scores[0]?.length ?? 0;
  // Dummy column r (index realCols + r) lets row r stay unassigned at weight 0.
  const cols = realCols + rows;

  // Minimize cost = −score (so weight 0 dummies are never preferred over
  // positive-score real columns, and always preferred over negatives).
  const cost = (r: number, c: number): number => {
    if (c >= realCols) return c - realCols === r ? 0 : Number.POSITIVE_INFINITY / 4;
    const s = (scores[r] as readonly number[])[c] as number;
    return s > 0 ? -s : Number.POSITIVE_INFINITY / 4;
  };

  // e-maxx potentials formulation, 1-indexed over rows i and columns j.
  const INF = Number.POSITIVE_INFINITY;
  const u = new Array<number>(rows + 1).fill(0);
  const v = new Array<number>(cols + 1).fill(0);
  const matchedRowAt = new Array<number>(cols + 1).fill(0); // column j → row i
  const way = new Array<number>(cols + 1).fill(0);

  for (let i = 1; i <= rows; i += 1) {
    matchedRowAt[0] = i;
    let j0 = 0;
    const minv = new Array<number>(cols + 1).fill(INF);
    const used = new Array<boolean>(cols + 1).fill(false);
    do {
      used[j0] = true;
      const i0 = matchedRowAt[j0] as number;
      let delta = INF;
      let j1 = 0;
      for (let j = 1; j <= cols; j += 1) {
        if (used[j]) continue;
        const cur = cost(i0 - 1, j - 1) - (u[i0] as number) - (v[j] as number);
        if (cur < (minv[j] as number)) {
          minv[j] = cur;
          way[j] = j0;
        }
        if ((minv[j] as number) < delta) {
          delta = minv[j] as number;
          j1 = j;
        }
      }
      for (let j = 0; j <= cols; j += 1) {
        if (used[j]) {
          u[matchedRowAt[j] as number] = (u[matchedRowAt[j] as number] as number) + delta;
          v[j] = (v[j] as number) - delta;
        } else {
          minv[j] = (minv[j] as number) - delta;
        }
      }
      j0 = j1;
    } while (matchedRowAt[j0] !== 0);
    do {
      const j1 = way[j0] as number;
      matchedRowAt[j0] = matchedRowAt[j1] as number;
      j0 = j1;
    } while (j0 !== 0);
  }

  const result = new Array<number | null>(rows).fill(null);
  for (let j = 1; j <= cols; j += 1) {
    const i = matchedRowAt[j] as number;
    if (i > 0 && j - 1 < realCols) {
      const s = (scores[i - 1] as readonly number[])[j - 1] as number;
      if (s > 0) result[i - 1] = j - 1;
    }
  }
  return result;
}
